// Who holds a booking — « Tenu par Vega » (`vegaHeld`) or the PMS's own
// (docs/PMS-OWNED-BOOKINGS-BRIEF.md §3, OB1).
//
// Until an établissement goes `live`, the Vega seed rewrites every booking it
// brought in every 30 minutes: dates, rooms, heads, status and cancellation. A
// desk act on such a booking is undone by the next run, so the booking is
// read-only here and its state is changed in Vega. Everything else — a booking
// typed at the desk, the website's, the phone flow's — is the PMS's own and
// already looks the way every booking will look after cutover.
//
// The same rule the cardex applies to a client (`pms_guest_vega_held`, S3) and
// the database applies to a booking (`pms_booking_vega_held`): a Vega number,
// and an établissement not yet live. At cutover every établissement goes
// `live`, the predicate is false everywhere, and the flag disappears by itself.

/** `app_property.mode`, checked by `app_property_mode_check`. */
/** `training` (OB5): the training établissement — never seeded from Vega, it
 *  behaves for the desk as `live` does after cutover. */
export const PMS_PROPERTY_MODES = ['shadow', 'pilot', 'live', 'training'] as const;
export type PmsPropertyMode = (typeof PMS_PROPERTY_MODES)[number];

/**
 * True when Vega holds this booking: it came from the seed and its
 * établissement is not live yet.
 *
 * `booking` undefined means the booking was not read — it counts as Vega's,
 * because « unknown » is not a licence to write (ADR-0028). `mode` undefined
 * means the établissement is unknown to the caller, and it counts as not live,
 * as `pms_booking_vega_held` does in SQL.
 */
export function bookingIsVegaHeld(
  booking: { sourceVegaResa?: string | null } | undefined,
  mode: PmsPropertyMode | undefined,
): boolean {
  if (booking === undefined) return true;
  if ((booking.sourceVegaResa ?? null) === null) return false;
  return mode !== 'live' && mode !== 'training';
}

/** The tag's tooltip, and the reason a Vega-held row offers no act. */
export const VEGA_HELD_TITLE_FR = 'Réservation tenue par Vega : son état se change dans Vega';

/** The band at the top of a Vega-held booking's form (Q1: read-only). */
export const VEGA_HELD_BAND_FR =
  'Réservation tenue par Vega : modifiez-la dans Vega. Ce que vous saisiriez ici serait remplacé ' +
  'à la prochaine synchronisation (toutes les 30 minutes), donc rien ne s’enregistre ici.';
